import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { ThumbsUp, ThumbsDown, CheckCircle2 } from "lucide-react";

/*
  AdvisorFeedback
  ---------------
  Outcome widget rendered under every advisor result. Two-step:
    Step 1 — quick helpful / not-helpful tap.
    Step 2 — optional follow-up: "what happened next?" + 4 yes/no toggles.
  Becomes the foundation for Stage C/D ontology evolution & archetype validation.
*/
export function AdvisorFeedback({ sessionId }) {
  const [existing, setExisting] = useState(null);
  const [stage, setStage] = useState("ask"); // ask | follow | done
  const [helpful, setHelpful] = useState(null);
  const [score, setScore] = useState(null);
  const [whatHappened, setWhatHappened] = useState("");
  const [toggles, setToggles] = useState({
    consistency_improved: null, communication_improved: null,
    follow_through: null, progressed: null,
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [bal, setBal] = useState({ missed_or_misattributed: null, understood_options: null, attempted: null, outcome: null });
  const Chips = ({ k, opts, testPrefix }) => (
    <div className="flex flex-wrap gap-1.5">
      {opts.map(([v, l]) => (
        <button key={String(v)} type="button" data-testid={`${testPrefix}-${v}`} onClick={() => setBal({ ...bal, [k]: v })}
          className={`px-2.5 py-1 rounded-full text-[11px] ${bal[k] === v ? "bg-mauve text-ivory" : "bg-ivory text-cocoa/65 border border-champagne"}`}>{l}</button>
      ))}
    </div>
  );

  useEffect(() => {
    if (!sessionId) return;
    api.get(`/advisor/feedback/${sessionId}`).then((r) => {
      if (r.data?.feedback) { setExisting(r.data.feedback); setStage("done"); }
    }).catch(() => {});
  }, [sessionId]);

  const submit = async (extra, toDone = false) => {
    setBusy(true); setErr("");
    try {
      const payload = { session_id: sessionId, helpful, score, ...extra };
      await api.post("/advisor/feedback", payload);
      setStage(toDone ? "done" : "follow");
    } catch (e) {
      setErr(e?.response?.data?.detail || "Couldn't save — try again.");
    } finally { setBusy(false); }
  };

  if (existing || stage === "done") {
    return (
      <div data-testid="advisor-feedback-done" className="ss-card p-4 bg-sage/15 flex items-center gap-2 text-cocoa/70 text-sm">
        <CheckCircle2 size={14} className="text-sage-dark" /> Thank you — your feedback shapes future reads.
      </div>
    );
  }

  if (stage === "ask") {
    return (
      <div data-testid="advisor-feedback" className="ss-card p-5">
        <p className="text-[10px] tracking-[0.3em] uppercase text-mauve">Was this useful?</p>
        <div className="mt-3 flex flex-wrap gap-2">
          <button
            data-testid="feedback-helpful"
            disabled={busy}
            onClick={() => { setHelpful(true); submit({ helpful: true }).then(() => setStage("follow")); }}
            className="ss-pill ss-pill-ghost h-10 text-xs disabled:opacity-50"
          ><ThumbsUp size={12} /> Useful</button>
          <button
            data-testid="feedback-not-helpful"
            disabled={busy}
            onClick={() => { setHelpful(false); submit({ helpful: false }).then(() => setStage("follow")); }}
            className="ss-pill ss-pill-ghost h-10 text-xs disabled:opacity-50"
          ><ThumbsDown size={12} /> Not quite</button>
          {[1, 2, 3, 4, 5].map((n) => (
            <button key={n}
              data-testid={`feedback-score-${n}`}
              disabled={busy}
              onClick={() => { setScore(n); submit({ score: n }).then(() => setStage("follow")); }}
              className="w-9 h-9 rounded-full bg-champagne/50 text-cocoa text-xs hover:bg-mauve hover:text-ivory transition-colors disabled:opacity-50"
            >{n}</button>
          ))}
        </div>
        {err && <p className="mt-2 text-rose text-xs">{err}</p>}
      </div>
    );
  }

  // Follow stage — optional outcome
  return (
    <div data-testid="advisor-feedback-follow" className="ss-card p-5">
      <p className="text-[10px] tracking-[0.3em] uppercase text-mauve">If you'd like — what happened next?</p>
      <textarea
        data-testid="feedback-what-happened"
        value={whatHappened}
        onChange={(e) => setWhatHappened(e.target.value)}
        maxLength={600}
        placeholder="Optional. A line or two. Private to your record."
        className="mt-3 w-full bg-ivory border border-champagne rounded-md p-3 text-cocoa text-sm focus:border-mauve outline-none resize-y min-h-[80px]"
      />
      <div className="mt-4 grid sm:grid-cols-2 gap-2 text-xs">
        {[
          { k: "consistency_improved", l: "Did consistency improve?" },
          { k: "communication_improved", l: "Did communication improve?" },
          { k: "follow_through", l: "Did follow-through happen?" },
          { k: "progressed", l: "Did the relationship progress?" },
        ].map((q) => (
          <div key={q.k} className="flex items-center justify-between bg-champagne/20 rounded-md px-3 py-2">
            <span className="text-cocoa/75">{q.l}</span>
            <div className="flex gap-1">
              <button data-testid={`feedback-${q.k}-yes`}  onClick={() => setToggles({ ...toggles, [q.k]: true })}  className={`px-2 py-0.5 rounded text-[10px] ${toggles[q.k] === true ? "bg-sage text-ivory" : "bg-ivory text-cocoa/55"}`}>Yes</button>
              <button data-testid={`feedback-${q.k}-no`}   onClick={() => setToggles({ ...toggles, [q.k]: false })} className={`px-2 py-0.5 rounded text-[10px] ${toggles[q.k] === false ? "bg-rose text-ivory" : "bg-ivory text-cocoa/55"}`}>No</button>
            </div>
          </div>
        ))}
      </div>
      <div className="mt-4 space-y-3 text-xs" data-testid="feedback-balanced">
        <div><p className="text-cocoa/75 mb-1.5">Did we miss something important, or put something on the wrong person?</p>
          <Chips k="missed_or_misattributed" testPrefix="feedback-missed" opts={[["no", "No"], ["missed_something", "Missed something"], ["wrong_person", "Wrong person"], ["unsure", "Unsure"]]} /></div>
        <div><p className="text-cocoa/75 mb-1.5">Were your own options and responsibilities clear?</p>
          <Chips k="understood_options" testPrefix="feedback-understood" opts={[[true, "Yes"], [false, "No"]]} /></div>
        <div><p className="text-cocoa/75 mb-1.5">Did you try any of it?</p>
          <Chips k="attempted" testPrefix="feedback-attempted" opts={[["yes", "Yes"], ["not_yet", "Not yet"], ["no", "No"]]} /></div>
        <div><p className="text-cocoa/75 mb-1.5">And so far?</p>
          <Chips k="outcome" testPrefix="feedback-outcome" opts={[["helpful_change", "Helpful change"], ["no_change", "No change"], ["worse_or_unsafe", "Worse / unsafe"], ["not_tried", "Not tried"], ["too_soon", "Too soon"], ["unknown", "Unknown"]]} /></div>
      </div>
      <div className="mt-4 flex gap-2 justify-end">
        <button data-testid="feedback-skip" onClick={() => setStage("done")} className="ss-pill ss-pill-ghost h-10 text-xs">Skip</button>
        <button
          data-testid="feedback-submit-follow"
          disabled={busy}
          onClick={() => submit({ what_happened: whatHappened || undefined, ...toggles, ...Object.fromEntries(Object.entries(bal).filter(([, v]) => v !== null)) }, true)}
          className="ss-pill ss-pill-primary h-10 text-xs disabled:opacity-50"
        >Send</button>
      </div>
      {err && <p className="mt-2 text-rose text-xs">{err}</p>}
    </div>
  );
}
